// What follows the timeline hero: the zoom into one sentence, the hours, and
// the deal. The zoom is driven by the scroll: the same timeline closes in from
// 50 minutes to one spoken sentence, then its layers come on one at a time.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LoopVideo, SectionHead } from '../components/primitives';
import { GROUP_COLOR, LANE_GROUP, drawTimeline, useTimeline, type Group, type TimelineData } from '../components/timeline';
import { SHOTS } from '../components/scenes';
import { tcOf } from './timeline-hero';

gsap.registerPlugin(ScrollTrigger);

/* ---------- THE ZOOM: 50 minutes down to one sentence ---------- */
// The sentence at 06:25 of Europe Has a Problem ("And from the 70s on, cheap
// gas from the USSR."). The window is 15 seconds wide, in tenths of a second.
const TARGET = { from: 3840, to: 3990 };
const SHOT = SHOTS.gears;

const STEPS: { name: string; g: Group[] }[] = [
  { name: 'Voice', g: ['vo'] },
  { name: 'Picture', g: ['ft', 'ai', 'alt', 'look'] },
  { name: 'Graphics', g: ['gfx'] },
  { name: 'Sound', g: ['sfx', 'ji'] },
  { name: 'Music', g: ['mus'] },
  { name: 'Subtitles', g: ['sub'] },
];
const DONE = STEPS.length; // every layer on

// how many clips overlap the window [a, b], on the lit layers
function clipsIn(d: TimelineData, a: number, b: number, only: Group[] | null) {
  let n = 0;
  d.lanes.forEach((row, i) => {
    if (only && !only.includes(LANE_GROUP[i])) return;
    for (let k = 0; k < row.length; k += 2) {
      if (row[k] >= b) break;
      if (row[k] + row[k + 1] > a) n += 1;
    }
  });
  return n;
}

const litAt = (step: number): Group[] | null => (step < 0 || step >= DONE ? null : STEPS.slice(0, step + 1).flatMap((s) => s.g));

// The sentence as a board: one row per layer, each clip in the window drawn
// where it sits (left and width in percent of the 15 seconds).
interface Block { left: number; width: number; color: string }
interface BoardRow { name: string; n: number; lanes: Block[][] }
function boardRows(d: TimelineData): BoardRow[] {
  const span = TARGET.to - TARGET.from;
  return STEPS.map((st) => {
    const lanes: Block[][] = [];
    let n = 0;
    d.lanes.forEach((row, i) => {
      const g = LANE_GROUP[i];
      if (!st.g.includes(g)) return;
      const blocks: Block[] = [];
      for (let k = 0; k < row.length; k += 2) {
        const s = row[k], e = s + row[k + 1];
        if (s >= TARGET.to) break;
        if (e <= TARGET.from) continue;
        const a = Math.max(s, TARGET.from), b = Math.min(e, TARGET.to);
        blocks.push({ left: ((a - TARGET.from) / span) * 100, width: ((b - a) / span) * 100, color: GROUP_COLOR[g] });
        n += 1;
      }
      if (blocks.length) lanes.push(blocks);
    });
    return { name: st.name, n, lanes };
  });
}

export function ZoomSentence() {
  const data = useTimeline();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const bandRef = useRef<HTMLSpanElement | null>(null);
  const fromRef = useRef<HTMLSpanElement | null>(null);
  const toRef = useRef<HTMLSpanElement | null>(null);
  const spanRef = useRef<HTMLElement | null>(null);
  const countRef = useRef<HTMLElement | null>(null);
  const prog = useRef(0);
  const stepRef = useRef(-1);
  const dataRef = useRef<TimelineData | null>(null);
  dataRef.current = data;
  const [step, setStep] = useState(-1); // -1: still zooming; 0..5: layers coming on; 6: all on
  const rows = useMemo(() => (data ? boardRows(data) : []), [data]);

  const paint = () => {
    const cv = cvRef.current, d = dataRef.current;
    if (!cv || !d) return;
    // the first half of the scroll is the zoom (exponential, so it reads as one
    // steady move); the window keeps the sentence in view the whole way in
    const z = Math.min(1, prog.current / 0.5);
    const e = z * z * (3 - 2 * z);
    const wide = d.end, tight = TARGET.to - TARGET.from;
    const span = Math.exp(Math.log(wide) + (Math.log(tight) - Math.log(wide)) * e);
    const t0 = Math.max(0, TARGET.from * (1 - (span - tight) / (wide - tight)));
    drawTimeline(cv, d, t0, t0 + span, null);
    // the bracket around the sentence: a hairline in the whole film, the full width at the end
    const band = bandRef.current;
    if (band) {
      band.style.left = `${(((TARGET.from - t0) / span) * 100).toFixed(3)}%`;
      band.style.width = `${((tight / span) * 100).toFixed(3)}%`;
    }
    if (fromRef.current) fromRef.current.textContent = tcOf(t0);
    if (toRef.current) toRef.current.textContent = tcOf(t0 + span);
    if (spanRef.current) spanRef.current.textContent = tcOf(Math.round(span / 10) * 10);
    if (countRef.current) countRef.current.textContent = clipsIn(d, t0, t0 + span, litAt(stepRef.current)).toLocaleString('en-US');
  };

  const apply = (p: number) => {
    prog.current = p;
    const s = p < 0.56 ? -1 : Math.min(DONE, Math.floor((p - 0.56) / 0.065));
    if (s !== stepRef.current) { stepRef.current = s; setStep(s); }
    paint();
  };

  useEffect(() => {
    paint();
    const cv = cvRef.current;
    if (!cv) return;
    const ro = new ResizeObserver(paint);
    ro.observe(cv);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useLayoutEffect(() => {
    // without motion the section shows its end: the sentence, every layer on
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { apply(1); return; }
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: rootRef.current, start: 'top top', end: '+=260%', pin: stageRef.current, scrub: 0.5,
        onUpdate: (self) => apply(self.progress),
      });
    }, rootRef);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const board = step >= 0;
  return (
    <div className="onz" id="zoom" ref={rootRef} role="region" aria-labelledby="onz-h">
      <div className="onz-stage" ref={stageRef}>
        <div className="onz-in">
          <div className="onz-headrow">
            <div className="onz-title">
              <span className="crw-eyebrow"><i></i>{board ? 'One sentence' : 'One film'}</span>
              <h2 id="onz-h" className="crw-h-grad onz-h">Every clip sits on a sentence.</h2>
            </div>
            <div className="onz-read" aria-hidden="true">
              <span><b ref={spanRef}>50:23</b>in view</span>
              <span><b ref={countRef}>5,487</b>clips</span>
            </div>
          </div>

          <div className="onz-panel">
            <div className="onz-ruler">
              <span ref={fromRef}>00:00</span>
              <span className="onz-film">Europe Has a Problem</span>
              <span ref={toRef}>50:23</span>
            </div>
            <div className="onz-view" data-board={board || undefined}>
              {/* the film, closing in on the sentence */}
              <div className="onz-canvas">
                <canvas ref={cvRef} role="img" aria-label="The timeline zooms from the whole film to one sentence at 06:25"></canvas>
                <span className="onz-band" ref={bandRef} aria-hidden="true"><em>06:25</em></span>
              </div>
              {/* the sentence: its shot, and its clips layer by layer */}
              <div className="onz-board" aria-hidden={!board}>
                <div className="onz-shot">
                  <div className="onz-screen">
                    <LoopVideo src={SHOT.src} poster={SHOT.poster} />
                    {/* on a phone the voice block is too short for the sentence, so it sits on the shot */}
                    <span className="onz-line" aria-hidden="true">“{SHOT.lines[0].text}”</span>
                  </div>
                  <span className="onz-cap">06:25 · translated from Bulgarian</span>
                </div>
                <ol className="onz-rows">
                  {rows.map((r, i) => (
                    <li key={r.name} className={step >= i ? 'onz-row-on' : undefined} style={{ flexGrow: Math.max(1, r.lanes.length) }}>
                      <span className="onz-rname"><b>{r.name}</b><i>{r.n} {r.n === 1 ? 'clip' : 'clips'}</i></span>
                      <span className="onz-rlanes">
                        {r.lanes.map((lane, li) => (
                          <span key={li} className="onz-lane">
                            {lane.map((b, bi) => (
                              <i key={bi} style={{ left: `${b.left}%`, width: `calc(${b.width}% - 2px)`, background: b.color, transitionDelay: `${li * 40 + bi * 50}ms` }}></i>
                            ))}
                            {i === 0 ? <em className="onz-say">“{SHOT.lines[0].text}”</em> : null}
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- THE HOURS: two bars, drawn to scale ---------- */
export function Hours() {
  return (
    <section id="hours" className="onhr">
      <div className="onhr-in">
        <h2 className="onhr-h"><span className="onhr-dim">160 hours by hand.</span> <span className="crw-h-grad">30 with us.</span></h2>
        <div className="onhr-bars" role="img" aria-label="About 160 hours of editing by hand, about 30 hours with us">
          <div className="onhr-row">
            <span className="onhr-name">By hand</span>
            <span className="onhr-track"><i className="onhr-bar" style={{ width: '100%' }}></i></span>
            <b>160 h</b>
          </div>
          <div className="onhr-row">
            <span className="onhr-name">With us</span>
            <span className="onhr-track"><i className="onhr-bar onhr-bar-us" style={{ width: '18.75%' }}></i></span>
            <b>30 h</b>
          </div>
        </div>
        <p className="onhr-note">Our estimate, from our first 50-minute film.</p>
      </div>
    </section>
  );
}

/* ---------- THE DEAL: three terms ---------- */
const TERMS: [string, string][] = [
  ['A third less', 'than you pay your editor now'],
  ['Every layer', 'footage, graphics, sound, music, subtitles'],
  ['Five working days', 'after your first film'],
];

export function Deal() {
  return (
    <section id="deal" style={{ padding: '90px 24px 40px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="The deal" title="Your next film, for a third less." />
        <div className="ond-grid">
          {TERMS.map(([big, small]) => (
            <div key={big} className="ond-card crw-glass crw-gradborder">
              <b className="crw-h-grad">{big}</b>
              <span>{small}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
